import type { TemplatePrompts } from '@kimuno/api-sdk';
import { useCallback, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { getGeneratedImagesRemainingLimit as getGeneratedImagesRemainingLimitAction } from '../../../actions/aiAssistant.actions';
import { getAiGeneratedImagesRemainingLimit } from '../../../reducers/aiAssistant.selectors';
import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';
import useAction from '../../../utils/useAction';
import { isImageGenerationTemplate } from './utils';

/**
 * The limit guards every template entry runs before it does anything: the
 * prompt limit, and the image-generation limit for image templates. Shared by
 * `useTemplateSelection` (KI Tools form opens) and the reader's unit entry
 * (which sends the request straight away), so the alerts and the "blocked"
 * outcome stay identical on both paths. Returns `canOpenTemplate`, which
 * surfaces the matching alert and answers false when a guard blocks.
 */
const useTemplatePromptGuards = () => {
	const { setImageGenerationLimitReachedAlertVisible, isPromptLimitReached, showPromptLimitAlert } =
		useContext(aiAssistantContext);

	const remainingLimit = useSelector(getAiGeneratedImagesRemainingLimit);
	const hasRemainingLimit = !!remainingLimit && remainingLimit > 0;
	const fetchRemainingLimit = useAction(getGeneratedImagesRemainingLimitAction);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		fetchRemainingLimit();
	}, []);

	const canOpenTemplate = useCallback(
		(id: TemplatePrompts): boolean => {
			if (isPromptLimitReached) {
				showPromptLimitAlert();
				return false;
			}

			if (isImageGenerationTemplate(id) && !hasRemainingLimit) {
				setImageGenerationLimitReachedAlertVisible(true);
				return false;
			}

			return true;
		},
		[
			isPromptLimitReached,
			showPromptLimitAlert,
			hasRemainingLimit,
			setImageGenerationLimitReachedAlertVisible,
		]
	);

	return canOpenTemplate;
};

export default useTemplatePromptGuards;
